import { useAppSelector } from 'app/hooks';
import { useGetConfidenceScoreQuery } from 'features/api/confidence-api';
import useIsDuctedPathway from 'hooks/use-is-ducted-pathway';
import { useEffect, useRef } from 'react';
import { ConfidenceScoreResultType } from 'utils/confidence-score';

// Live confidence score for the current project (AHA-6796). Skips off the
// ducted pathway (mirrors the EE-page skip) and refetches ~500ms after
// either autosave completion signal — equipmentPersistVersion (existing
// equipment, bumped on EVERY successful save so spec-only edits refresh
// the score too) or settingsSaveVersion (customer preferences) — so the
// score always reflects persisted state, never in-flight edits. Returns
// null until an applicable score is available.
export default function useConfidenceScore(): ConfidenceScoreResultType | null {
  const isDuctedPathway = useIsDuctedPathway();
  const projectId = useAppSelector(
    (state) => state.project.projectDetails?.projectId
  );
  const structureUniqueId = useAppSelector(
    (state) => state.structure.structureUniqueId
  );
  const equipmentPersistVersion = useAppSelector(
    (state) => state.structure.equipmentPersistVersion
  );
  const settingsSaveVersion = useAppSelector(
    (state) => state.project.settingsSaveVersion
  );

  const { data, refetch, isUninitialized } = useGetConfidenceScoreQuery(
    {
      projectId: projectId as number,
      structureUniqueId: structureUniqueId ?? undefined
    },
    { skip: !isDuctedPathway || !projectId }
  );

  // Debounced refetch keyed on the save-completion nonces. The initial
  // mount is served by the query itself — skip the first effect run.
  const initialVersionsRef = useRef({
    equipment: equipmentPersistVersion,
    settings: settingsSaveVersion
  });
  useEffect(() => {
    if (isUninitialized) return undefined;
    const initial = initialVersionsRef.current;
    if (
      equipmentPersistVersion === initial.equipment &&
      settingsSaveVersion === initial.settings
    ) {
      return undefined;
    }
    const timer = setTimeout(() => {
      refetch();
    }, 500);
    return () => clearTimeout(timer);
  }, [equipmentPersistVersion, settingsSaveVersion, isUninitialized, refetch]);

  return data?.applicable ? data : null;
}
